import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { ProcedureDocumentType } from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { ORDERING_PROVIDER_PROMPT } from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const procedureTypeDisplay: Record<ProcedureDocumentType, string> = {
  [ProcedureDocumentType.CARDIAC]: "Cardiac",
  [ProcedureDocumentType.GI]: "GI/EGD",
  [ProcedureDocumentType.SLEEP]: "Sleep Study",
  [ProcedureDocumentType.PROCEDURE]: "Procedure",
  [ProcedureDocumentType.REHAB]: "Rehabilitation",
  [ProcedureDocumentType.CARDIAC_VEIN]: "Cardiac Venous",
  [ProcedureDocumentType.DEVICE_REQUEST]: "Device Request",
  [ProcedureDocumentType.DEVICE_INFO]: "Device Information",
  [ProcedureDocumentType.INVENTORY_LIST]: "Inventory List",
};

export const procedureTypeSchema = z.nativeEnum(ProcedureDocumentType);

const baseProcedureFields = {
  patient: patientSchema,
};

const extendedProcedureSchema = z.object({
  isAdw: isAdwSchema,
  name: z.string().nullable(),
  inProgress: z.boolean().nullable(),
  orderingProvider: practitionerSchema.nullable().describe(ORDERING_PROVIDER_PROMPT),
  receivingProvider: practitionerSchema.nullable(),
  procedure: z.string().nullable(),
  ahi: z.number().nullable(),
  recommendation: z.string().nullable(),
  summaryStatements: z.string().nullable(),
  studyType: z.string().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  isSigned: z.boolean().nullable(),
  ...DocumentDateSchema.shape,
});

const procedureCustomFields: Record<ProcedureDocumentType, Record<string, z.ZodTypeAny>> = {
  [ProcedureDocumentType.CARDIAC]: extendedProcedureSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    name: true,
    procedure: true,
    receivingProvider: true,
  }).shape,
  [ProcedureDocumentType.GI]: extendedProcedureSchema.pick({
    dateOfEvent: true,
    inProgress: true,
    orderingProvider: true,
  }).shape,
  [ProcedureDocumentType.SLEEP]: extendedProcedureSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    dateReceived: true,
    inProgress: true,
    orderingProvider: true,
    receivingProvider: true,
    ahi: true,
    recommendation: true,
    summaryStatements: true,
    studyType: true,
    isSigned: true,
  }).shape,
  [ProcedureDocumentType.PROCEDURE]: extendedProcedureSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    facilityType: true,
    inProgress: true,
  }).shape,
  [ProcedureDocumentType.REHAB]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.CARDIAC_VEIN]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.DEVICE_REQUEST]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.DEVICE_INFO]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.INVENTORY_LIST]: extendedProcedureSchema.shape,
};

const makeProcedureSchema = (type: ProcedureDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseProcedureFields,
    ...customFields,
  });

type ProcedureSchemas = Record<ProcedureDocumentType, ReturnType<typeof makeProcedureSchema>> & {
  [ProcedureDocumentType.PROCEDURE]: ReturnType<typeof makeProcedureSchema> & {
    shape: { facilityType: typeof extendedProcedureSchema.shape.facilityType };
  };
};

export const procedureSchemas = (Object.values(ProcedureDocumentType) as ProcedureDocumentType[]).reduce(
  (acc, type) => {
    acc[type] = makeProcedureSchema(type, procedureCustomFields[type]);

    return acc;
  },
  {} as Record<ProcedureDocumentType, ReturnType<typeof makeProcedureSchema>>
) as ProcedureSchemas;

const procedureSchemasArray = (Object.values(ProcedureDocumentType) as ProcedureDocumentType[]).map(
  (type) => procedureSchemas[type]
) as [ReturnType<typeof makeProcedureSchema>, ...ReturnType<typeof makeProcedureSchema>[]];

export const anyProcedureSchema = z.discriminatedUnion("type", procedureSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Procedure = ExtractedDocumentFields<
  ProcedureDocumentType,
  typeof extendedProcedureSchema,
  typeof baseProcedureFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const procedureShape: Procedure = {
  id: "",
  type: ProcedureDocumentType.PROCEDURE,
  patient: null,
  name: null,
  inProgress: null,
  orderingProvider: null,
  receivingProvider: null,
  procedure: null,
  ahi: null,
  recommendation: null,
  summaryStatements: null,
  studyType: null,
  facilityType: null,
  isSigned: null,
  dateOfEvent: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
